import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Field, Loading, ErrorState, EmptyState, SelectField } from '../../components/ui.tsx';
import { AnalyticsIcon } from '../../components/NavIcons.tsx';
import { useAudit, AUDIT_VIA, type AuditEntry, type AuditFilters, type AuditVia } from '../../api/audit.ts';
import { useTeam } from '../../api/org.ts';
import { formatRelative } from '../../lib/format.ts';

// Entity types offered in the filter — a curated superset of what the log records.
const ENTITY_TYPES = [
  'screen',
  'playlist',
  'schedule',
  'media',
  'group',
  'layout',
  'overlay',
  'broadcast',
  'member',
  'report',
] as const;

const VIA_LABEL: Record<AuditVia, string> = {
  ui: 'Studio',
  assistant: 'Assistant',
  mcp: 'MCP',
  api: 'API key',
};
const VIA_TONE: Record<AuditVia, 'info' | 'ok' | 'warn' | 'neutral'> = {
  ui: 'neutral',
  assistant: 'info',
  mcp: 'ok',
  api: 'warn',
};

/** Humanize "schedule.create" → "Schedule created" style, but keep it readable
 *  even for unknown verbs (never a raw dotted token). */
function humanizeAction(action: string): string {
  const [entity, verb] = action.split('.');
  if (!verb) return action;
  const noun = entity!.replace(/_/g, ' ');
  const v = verb.replace(/_/g, ' ');
  return `${noun} ${v}`.replace(/^\w/, (c) => c.toUpperCase());
}

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 6)}…` : id;
}

/** Actor label: the user's name, or the origin when there's no user (API key etc). */
function actorLabel(e: AuditEntry): string {
  if (e.user) return e.user.name;
  return VIA_LABEL[e.via] ?? e.via;
}

/** A deep-link to the entity when it still exists (has a name) and the type has a
 *  page; otherwise the entity's name or a "type · shortid" fallback (no link). */
function EntityCell({ e }: { e: AuditEntry }): ReactNode {
  const label = e.entity_name ?? `${e.entity_type} · ${shortId(e.entity_id)}`;
  // No link for deleted (null name) entities, or types without a dedicated page.
  if (e.entity_name) {
    if (e.entity_type === 'screen')
      return <Link to="/screens" search={{ screen: e.entity_id }} className="cell-strong">{label}</Link>;
    if (e.entity_type === 'schedule')
      return <Link to="/schedules" search={{ edit: e.entity_id }} className="cell-strong">{label}</Link>;
    if (e.entity_type === 'media')
      return <Link to="/media" search={{ media: e.entity_id }} className="cell-strong">{label}</Link>;
    if (e.entity_type === 'playlist')
      return <Link to="/playlists/$playlistId" params={{ playlistId: e.entity_id }} className="cell-strong">{label}</Link>;
  }
  return <span className="cell-strong">{label}</span>;
}

function ViaBadge({ via }: { via: AuditVia }) {
  return <Badge tone={VIA_TONE[via] ?? 'neutral'}>{VIA_LABEL[via] ?? via}</Badge>;
}

function TimeCell({ ts }: { ts: number }) {
  return (
    <time dateTime={new Date(ts).toISOString()} title={new Date(ts).toLocaleString()}>
      {formatRelative(ts)}
    </time>
  );
}

function Details({ e }: { e: AuditEntry }) {
  if (!e.details_json || Object.keys(e.details_json).length === 0) return null;
  return (
    <details className="activity-details">
      <summary>Details</summary>
      <pre>{JSON.stringify(e.details_json, null, 2)}</pre>
    </details>
  );
}

/**
 * Settings → Activity: a read-only audit trail (admin/owner only — hidden for
 * everyone else). Keyset "Load more" pagination, filterable by entity type,
 * origin, teammate and date range. Renders a table on desktop and a card list on
 * phones (the table's columns don't fit narrow viewports).
 */
export function ActivitySection() {
  const [entityType, setEntityType] = useState('');
  const [via, setVia] = useState('');
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filters = useMemo<AuditFilters>(
    () => ({
      ...(entityType ? { entity_type: entityType } : {}),
      ...(via ? { via: via as AuditVia } : {}),
      ...(userId ? { user_id: userId } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [entityType, via, userId, from, to],
  );

  const audit = useAudit(filters);
  const team = useTeam();
  const entries = useMemo(() => audit.data?.pages.flatMap((p) => p.items) ?? [], [audit.data]);

  return (
    <div className="card card-pad">
      <div className="card-title">Activity</div>
      <p className="hint mb-4">Who changed what, and where it came from. Read-only.</p>

      <div className="activity-filters">
        <SelectField label="Type" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
          <option value="">All types</option>
          {ENTITY_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace(/^\w/, (c) => c.toUpperCase())}
            </option>
          ))}
        </SelectField>
        <SelectField label="Source" value={via} onChange={(e) => setVia(e.target.value)}>
          <option value="">All sources</option>
          {AUDIT_VIA.map((v) => (
            <option key={v} value={v}>
              {VIA_LABEL[v]}
            </option>
          ))}
        </SelectField>
        <SelectField label="Person" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Everyone</option>
          {(team.data?.members ?? []).map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.name}
            </option>
          ))}
        </SelectField>
        <Field label="From">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date" />
        </Field>
        <Field label="To">
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date" />
        </Field>
      </div>

      {audit.isLoading ? (
        <Loading />
      ) : audit.isError ? (
        <ErrorState message="Could not load the activity log." onRetry={() => audit.refetch()} />
      ) : entries.length === 0 ? (
        <EmptyState icon={<AnalyticsIcon />} title="No activity yet">
          <p className="text-dim">Nothing matches these filters.</p>
        </EmptyState>
      ) : (
        <>
          {/* Desktop table */}
          <div className="table-wrap activity-desktop">
            <table className="data">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Source</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="cell-dim"><TimeCell ts={e.created_at} /></td>
                    <td>{actorLabel(e)}</td>
                    <td><ViaBadge via={e.via} /></td>
                    <td>{humanizeAction(e.action)}</td>
                    <td><EntityCell e={e} /></td>
                    <td><Details e={e} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="activity-cards">
            {entries.map((e) => (
              <li key={e.id} className="activity-card">
                <div className="activity-card-head">
                  <ViaBadge via={e.via} />
                  <span className="cell-dim"><TimeCell ts={e.created_at} /></span>
                </div>
                <div className="activity-card-body">
                  <strong>{humanizeAction(e.action)}</strong>
                  <div className="cell-dim">
                    {actorLabel(e)} · <EntityCell e={e} />
                  </div>
                  <Details e={e} />
                </div>
              </li>
            ))}
          </ul>

          {audit.hasNextPage && (
            <div className="mt-4">
              <Button onClick={() => audit.fetchNextPage()} disabled={audit.isFetchingNextPage}>
                {audit.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
